// PDF exporter for the layout designer.
// Produces a multi-page report:
//   1. Cover — project meta, client, floor totals
//   2. 2D floor plan (native jsPDF vector drawing, not a screenshot)
//   3–5. 3D camera views — Isometric, Front elevation, Side elevation
//   6. Top-down photo-render (from 3D scene)
//   7. Bill of Quantities table (dimension-grouped rows)
//   8. Summary totals + notes
//
// Uses jspdf + jspdf-autotable. 3D snapshots come from window.__EDITOR_3D_SNAPSHOT__
// (installed by Canvas3D) which supports named camera views + high-res rendering.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { computeBOM, computeTotals, normaliseState } from "./bom";
import { OBJECT_DEFS, objectFootprint } from "../objectDefs";

const BRAND_RED   = [224, 74, 47];   // #e04a2f
const BRAND_GREEN = [74, 155, 63];   // #4a9b3f
const INK         = [30, 30, 30];
const MUTED       = [110, 110, 110];
const CREAM       = [247, 244, 238]; // #f7f4ee
const WALL_INK    = [40, 40, 40];

function drawFrame(doc, title, subtitle) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  // Top red rule
  doc.setFillColor(...BRAND_RED);
  doc.rect(0, 0, w, 4, "F");
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(0, 4, w, 1.2, "F");
  // Header text
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(title, 15, 15);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.setFontSize(9);
    doc.text(subtitle, w - 15, 15, { align: "right" });
  }
  // Footer
  doc.setDrawColor(220);
  doc.line(15, h - 15, w - 15, h - 15);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("Adarsh Udyog · Warehouse & Supermarket Layout Designer", 15, h - 9);
  const pageNum = `Page ${doc.internal.getCurrentPageInfo().pageNumber}`;
  doc.text(pageNum, w - 15, h - 9, { align: "right" });
}

function coverPage(doc, state, totals) {
  const w = doc.internal.pageSize.getWidth();
  drawFrame(doc, "Layout Design Report", new Date().toLocaleDateString());

  // Title block
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...INK);
  doc.text(state.projectName || "Untitled project", 15, 45);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...MUTED);
  doc.text(state.clientName ? `Client · ${state.clientName}` : "No client on file", 15, 54);

  // Colored keyline stripes
  doc.setFillColor(...BRAND_RED);
  doc.rect(15, 60, 40, 1.4, "F");
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(58, 60, 15, 1.4, "F");

  // Meta grid — kept minimal per user request
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const meta = [
    ["Module", (state.projectType || "warehouse").toUpperCase()],
    ["Units", (state.units || "mm").toUpperCase()],
    ["Ceiling", `${state.ceilingHeight || 0} mm`],
    ["Floor area", `${totals.floorArea_m2.toFixed(2)} m²`],
    ["Total rack bays", `${totals.rackBays}`],
    ["Total units", `${totals.totalUnits}`],
  ];
  let y = 78;
  const colW = (w - 30) / 3;
  for (let i = 0; i < meta.length; i++) {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = 15 + col * colW;
    const yy = y + row * 20;
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(meta[i][0].toUpperCase(), x, yy);
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(meta[i][1], x, yy + 7);
  }

  // Prepared-by / signature block
  const foot = doc.internal.pageSize.getHeight() - 40;
  doc.setDrawColor(220);
  doc.line(15, foot, w - 15, foot);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("Prepared by", 15, foot + 6);
  doc.text("Approved by", w / 2, foot + 6);
  doc.line(15, foot + 20, w / 2 - 10, foot + 20);
  doc.line(w / 2, foot + 20, w - 15, foot + 20);
}

/**
 * Draw the 2D floor plan natively on the PDF as a proper architectural
 * drawing — polygon walls, doors, objects with labels, dimensions and a
 * title block. No html2canvas / screenshot; everything is vector.
 *
 *   doc   — jsPDF instance
 *   state — normalised project state
 */
function drawPlanPage(doc, state) {
  doc.addPage();
  drawFrame(doc, "2D Floor Plan", `${(state.units || "mm").toUpperCase()} · plan view`);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 15, marginR = 15, marginT = 30, marginB = 45;
  const boxW = pageW - marginL - marginR;
  const boxH = pageH - marginT - marginB;

  const verts = state.floor?.vertices || [];
  if (verts.length < 3) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...MUTED);
    doc.text("No floor plan defined.", marginL, marginT + 10);
    return;
  }

  // Bounding box in mm
  const xs = verts.map((v) => v.x), ys = verts.map((v) => v.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const planW = maxX - minX;
  const planH = maxY - minY;
  // Scale (page-mm per plan-mm). Use conservative fit with headroom for labels.
  const scale = Math.min(boxW / planW, boxH / planH) * 0.86;
  const drawnW = planW * scale;
  const drawnH = planH * scale;
  const originX = marginL + (boxW - drawnW) / 2;
  const originY = marginT + (boxH - drawnH) / 2;
  const toPage = (x, y) => [originX + (x - minX) * scale, originY + (y - minY) * scale];

  // Cream floor fill (rectangle for rectangular polygons, generic path
  // otherwise). `doc.rect` fills reliably; use it for the common rectangle
  // case so we always see the floor.
  doc.setFillColor(...CREAM);
  doc.setDrawColor(...WALL_INK);
  doc.setLineWidth(1.4);
  const poly = verts.map((v) => toPage(v.x, v.y));
  if (verts.length === 4) {
    // Rectangle — use axis-aligned bounding rect (safe fast path).
    const px = poly.map((p) => p[0]);
    const py = poly.map((p) => p[1]);
    const rx = Math.min(...px), ry = Math.min(...py);
    doc.rect(rx, ry, Math.max(...px) - rx, Math.max(...py) - ry, "FD");
  } else {
    drawClosedPolygon(doc, poly, true, true);
  }

  // Faint grid inside the polygon (1 m grid).
  doc.setDrawColor(200);
  doc.setLineWidth(0.15);
  const gridMm = 1000;
  for (let gx = Math.ceil(minX / gridMm) * gridMm; gx <= maxX; gx += gridMm) {
    const [x1, y1] = toPage(gx, minY);
    const [x2, y2] = toPage(gx, maxY);
    doc.line(x1, y1, x2, y2);
  }
  for (let gy = Math.ceil(minY / gridMm) * gridMm; gy <= maxY; gy += gridMm) {
    const [x1, y1] = toPage(minX, gy);
    const [x2, y2] = toPage(maxX, gy);
    doc.line(x1, y1, x2, y2);
  }

  // Wall boundary (thick) + wall length labels
  doc.setDrawColor(...WALL_INK);
  doc.setLineWidth(1.6);
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const [ax, ay] = toPage(a.x, a.y);
    const [bx, by] = toPage(b.x, b.y);
    doc.line(ax, ay, bx, by);
    // Label wall length at midpoint slightly outside the polygon
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    // Normal pointing "outward" from centroid
    const cx = xs.reduce((s, v) => s + v, 0) / xs.length;
    const cy = ys.reduce((s, v) => s + v, 0) / ys.length;
    let nx = -(b.y - a.y), ny = (b.x - a.x);
    const nlen = Math.hypot(nx, ny) || 1;
    nx /= nlen; ny /= nlen;
    if ((mx - cx) * nx + (my - cy) * ny < 0) { nx = -nx; ny = -ny; }
    const [lx, ly] = toPage(mx + nx * 1500 / scale, my + ny * 1500 / scale);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...INK);
    doc.text(`${Math.round(len)} mm`, lx, ly, { align: "center" });
  }

  // Doors — coloured segments interrupting the wall.
  const doorPalette = { entry: BRAND_GREEN, exit: [30, 130, 200], loading: [230, 140, 40], fire_exit: BRAND_RED };
  for (const d of (state.floor?.doors || [])) {
    const a = verts[d.edge_idx];
    const b = verts[(d.edge_idx + 1) % verts.length];
    if (!a || !b) continue;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
    const off = Math.max(0, Math.min(len - d.width, d.offset || 0));
    const p1x = a.x + ux * off, p1y = a.y + uy * off;
    const p2x = a.x + ux * (off + d.width), p2y = a.y + uy * (off + d.width);
    const [sx, sy] = toPage(p1x, p1y);
    const [ex, ey] = toPage(p2x, p2y);
    const col = doorPalette[d.kind] || doorPalette.entry;
    // erase existing wall segment first with cream
    doc.setDrawColor(...CREAM);
    doc.setLineWidth(2.2);
    doc.line(sx, sy, ex, ey);
    doc.setDrawColor(...col);
    doc.setLineWidth(1.9);
    doc.line(sx, sy, ex, ey);
    // small label
    doc.setFontSize(6);
    doc.setTextColor(...col);
    const mid = [(sx + ex) / 2, (sy + ey) / 2 - 1.2];
    doc.text(String(d.kind || "door").replace("_", " ").toUpperCase(), mid[0], mid[1], { align: "center" });
  }

  // Obstacles — light grey hatched
  for (const ob of (state.floor?.obstacles || [])) {
    const [ox, oy] = toPage(ob.x, ob.y);
    const w = ob.length * scale;
    const h = ob.width * scale;
    doc.setFillColor(180, 180, 180);
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(0.4);
    doc.rect(ox, oy, w, h, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.setTextColor(50, 50, 50);
    doc.text(String(ob.label || ob.kind || "").toUpperCase(), ox + w / 2, oy + h / 2 + 1.2, { align: "center" });
  }

  // Objects — colored rects with rotation + label
  for (const obj of (state.objects || [])) {
    const def = OBJECT_DEFS[obj.type];
    if (!def) continue;
    if (!obj.position || !Number.isFinite(obj.position.x) || !Number.isFinite(obj.position.y)) continue;
    const fp = objectFootprint(obj);
    if (!Number.isFinite(fp.length) || !Number.isFinite(fp.width)) continue;
    const cx = obj.position.x + fp.length / 2;
    const cy = obj.position.y + fp.width / 2;
    const [pcx, pcy] = toPage(cx, cy);
    // Rotation is stored as {x, y, z} — use Y (yaw around vertical). Fall back
    // to 0 for legacy numeric values or missing rotation.
    let rotDeg = 0;
    if (typeof obj.rotation === "number") rotDeg = obj.rotation;
    else if (obj.rotation && typeof obj.rotation === "object") rotDeg = obj.rotation.y || 0;
    const w = fp.length * scale;
    const h = fp.width * scale;
    const color = hexToRgb(obj.color || def.color || "#e04a2f");
    // Draw the fill first, from a CLEAN state. Some jsPDF versions had a bug
    // where setting draw-color/lineWidth after setFillColor reset the fill.
    doc.setFillColor(color[0], color[1], color[2]);
    if (rotDeg === 0) {
      // Enlarge rectangles to at least 3mm × 3mm on paper so racks/MHE are
      // discernible at typical architectural scales.
      const wPad = Math.max(w, 3);
      const hPad = Math.max(h, 3);
      doc.rect(pcx - wPad / 2, pcy - hPad / 2, wPad, hPad, "F");
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.rect(pcx - wPad / 2, pcy - hPad / 2, wPad, hPad, "S");
    } else {
      const rad = (rotDeg * Math.PI) / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      const hL = w / 2, hW = h / 2;
      const corners = [
        [-hL, -hW], [hL, -hW], [hL, hW], [-hL, hW],
      ].map(([lx, ly]) => [pcx + lx * cos - ly * sin, pcy + lx * sin + ly * cos]);
      drawClosedPolygon(doc, corners, true, true);
    }
    if (w > 4 && h > 3) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor(255, 255, 255);
      const label = def.label + (obj.type === "pallet_rack" && obj.props?.num_bays
        ? ` (${obj.props.num_bays}B)` : "");
      doc.text(label, pcx, pcy + 1.5, { align: "center" });
    }
  }

  // Overall dimension lines (top + right side)
  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.3);
  const [tlx, tly] = toPage(minX, minY);
  const [trx, ] = toPage(maxX, minY);
  const [, bly] = toPage(minX, maxY);
  const dimY = tly - 6;
  doc.line(tlx, dimY, trx, dimY);
  doc.line(tlx, dimY - 1.2, tlx, dimY + 1.2);
  doc.line(trx, dimY - 1.2, trx, dimY + 1.2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...INK);
  doc.text(`${planW} mm  (${(planW / 1000).toFixed(2)} m)`, (tlx + trx) / 2, dimY - 1.5, { align: "center" });
  const dimX = trx + 6;
  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.3);
  doc.line(dimX, tly, dimX, bly);
  doc.line(dimX - 1.2, tly, dimX + 1.2, tly);
  doc.line(dimX - 1.2, bly, dimX + 1.2, bly);
  doc.text(`${planH} mm  (${(planH / 1000).toFixed(2)} m)`, dimX + 2, (tly + bly) / 2, { angle: 90 });

  // Title / scale block bottom-right
  const tbW = 70, tbH = 22;
  const tbX = pageW - marginR - tbW;
  const tbY = pageH - marginB;
  doc.setDrawColor(...INK);
  doc.setLineWidth(0.4);
  doc.rect(tbX, tbY, tbW, tbH);
  doc.setFillColor(...BRAND_RED);
  doc.rect(tbX, tbY, tbW, 3.2, "F");
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(tbX + tbW - 10, tbY, 10, 3.2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text(state.projectName || "Untitled", tbX + 3, tbY + 8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text(`Client · ${state.clientName || "-"}`, tbX + 3, tbY + 12);
  doc.text(`Module · ${(state.projectType || "warehouse").toUpperCase()}`, tbX + 3, tbY + 15);
  // Scale ratio: 1 paper mm represents N world mm. Round to a nearby common
  // architectural scale (100, 150, 200, 250, 500, 1000, 2500 …) so the label
  // reads like a real drawing.
  const rawRatio = 1 / scale;
  const commonScales = [50, 75, 100, 125, 150, 200, 250, 500, 750, 1000, 1500, 2000, 2500, 5000, 10000, 20000, 50000];
  let scaleN = commonScales[0];
  for (const s of commonScales) { if (s <= rawRatio) scaleN = s; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...INK);
  doc.text(`SCALE 1 : ${scaleN} (approx)`, tbX + 3, tbY + 19.5);

  // North arrow (simple triangle in the top-right of the drawing area).
  const naX = trx - 6, naY = tly + 8;
  doc.setDrawColor(...INK);
  doc.setFillColor(...INK);
  doc.setLineWidth(0.3);
  doc.triangle(naX, naY - 4, naX - 2.2, naY + 2.4, naX + 2.2, naY + 2.4, "F");
  doc.setFontSize(7);
  doc.text("N", naX, naY + 5.6, { align: "center" });

  // Legend (bottom-left) — colour → object type
  const legend = collectLegend(state);
  if (legend.length) {
    const lgX = marginL, lgY = pageH - marginB;
    doc.setDrawColor(...INK);
    doc.setLineWidth(0.3);
    doc.rect(lgX, lgY, 60, Math.min(22, 4 + legend.length * 3.2));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...INK);
    doc.text("LEGEND", lgX + 2, lgY + 3.2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    legend.slice(0, 6).forEach((item, i) => {
      const rgb = hexToRgb(item.color);
      doc.setFillColor(...rgb);
      doc.rect(lgX + 2, lgY + 5.5 + i * 3, 2.5, 2.2, "F");
      doc.setTextColor(...INK);
      doc.text(item.label, lgX + 6, lgY + 7.4 + i * 3);
    });
  }
}

/** Draw a filled/stroked closed polygon on a jsPDF instance using the public
 *  `doc.lines()` API (works reliably for fill+stroke in all jsPDF versions).
 *  `pts` is an absolute-coordinate array `[[x,y], …]`. */
function drawClosedPolygon(doc, pts, fill, stroke) {
  if (!pts || pts.length < 3) return;
  for (const p of pts) {
    if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return;
  }
  // Vectors are deltas between consecutive points.
  const vecs = [];
  for (let i = 1; i < pts.length; i++) {
    vecs.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
  }
  const style = fill && stroke ? "FD" : fill ? "F" : "S";
  doc.lines(vecs, pts[0][0], pts[0][1], [1, 1], style, true);
}

function collectLegend(state) {
  const seen = new Map();
  for (const obj of (state.objects || [])) {
    const def = OBJECT_DEFS[obj.type];
    if (!def) continue;
    if (!seen.has(obj.type)) {
      seen.set(obj.type, { label: def.label, color: obj.color || def.color || "#e04a2f" });
    }
  }
  return Array.from(seen.values());
}

function hexToRgb(hex) {
  const h = (hex || "#e04a2f").replace("#", "");
  return [
    parseInt(h.substr(0, 2), 16),
    parseInt(h.substr(2, 2), 16),
    parseInt(h.substr(4, 2), 16),
  ];
}

function snap3D(view = null, bounds = null) {
  if (typeof window.__EDITOR_3D_SNAPSHOT__ === "function") {
    return window.__EDITOR_3D_SNAPSHOT__({ view, bounds });
  }
  return null;
}

/**
 * Compute the world-space bounds of the warehouse from the state polygon +
 * wall height + object positions. World units are metres (MM = 0.001).
 */
function computeBounds(state) {
  const MM = 0.001;
  const verts = state.floor?.vertices || [];
  if (!verts.length) return null;
  const xs = verts.map((v) => v.x * MM);
  const zs = verts.map((v) => v.y * MM);
  const wallH = (state.floor?.wall_height || 6000) * MM;
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minZ = Math.min(...zs), maxZ = Math.max(...zs);
  return {
    center: { x: (minX + maxX) / 2, y: wallH / 2, z: (minZ + maxZ) / 2 },
    size:   { x: maxX - minX, y: wallH, z: maxZ - minZ },
  };
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

async function addImagePage(doc, title, subtitle, dataUrl) {
  doc.addPage();
  drawFrame(doc, title, subtitle);
  if (!dataUrl) {
    doc.setTextColor(...MUTED);
    doc.setFontSize(11);
    doc.text("Snapshot unavailable — open the corresponding view before exporting.", 20, 60);
    return;
  }
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const maxW = w - 30;
  const maxH = h - 60;
  let iw = 1600, ih = 1000;
  try {
    const img = await loadImage(dataUrl);
    iw = img.naturalWidth || iw;
    ih = img.naturalHeight || ih;
  } catch (e) {
    console.warn("image preload failed", e);
  }
  const scale = Math.min(maxW / iw, maxH / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = (w - dw) / 2;
  const dy = 25 + (maxH - dh) / 2;
  doc.addImage(dataUrl, "PNG", dx, dy, dw, dh, undefined, "FAST");
}

function bomTable(doc, rows) {
  doc.addPage();
  drawFrame(doc, "Bill of Quantities", "Grouped by dimension");
  autoTable(doc, {
    startY: 25,
    head: [["Item", "Dimensions (H × D × L mm)", "Qty (bays / units)"]],
    body: rows.map((r) => [r.label, r.dimStr, r.qty]),
    theme: "grid",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: INK, lineColor: [230, 230, 230] },
    headStyles: { fillColor: BRAND_RED, textColor: 255, fontSize: 10.5, halign: "left" },
    alternateRowStyles: { fillColor: [250, 247, 240] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 90 },
      1: { cellWidth: 100, halign: "center" },
      2: { halign: "right", cellWidth: 40, fontStyle: "bold" },
    },
    didDrawPage: () => drawFrame(doc, "Bill of Quantities", "Grouped by dimension"),
    margin: { top: 25, bottom: 20 },
  });
}

function summaryPage(doc, state, totals, rows) {
  doc.addPage();
  drawFrame(doc, "Summary & Compliance", state.projectName);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...INK);
  doc.text("Project totals", 15, 40);

  const stats = [
    ["Unique BoQ lines", rows.length],
    ["Total rack bays", totals.rackBays],
    ["Total units (all items)", totals.totalUnits],
    ["Floor area", `${totals.floorArea_m2.toFixed(2)} m²`],
  ];
  autoTable(doc, {
    startY: 46,
    body: stats,
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 80, textColor: MUTED },
      1: { textColor: INK },
    },
  });

  // Doors + compliance notes
  const doors = state.floor?.doors || [];
  const compl = state.compliance || {};
  const notesY = doc.lastAutoTable.finalY + 15;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Site notes", 15, notesY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const noteLines = [
    `Doors placed on plan: ${doors.length} (${doors.filter((d) => d.kind === "entry").length} entry, ${doors.filter((d) => d.kind === "loading").length} loading).`,
    compl.enabled
      ? `Compliance check ACTIVE for ${compl.preset || "custom"} · min aisle ${compl.threshold} mm.`
      : "Compliance check not enabled at time of export.",
    `Design units: ${(state.units || "mm").toUpperCase()}; ceiling clearance ${state.ceilingHeight || "-"} mm.`,
    "All layouts and dimensions are indicative. Final rack specifications, load capacity, structural safety, and site suitability must be verified by our authorised technical team before finalising.",
  ];
  let ty = notesY + 8;
  for (const line of noteLines) {
    const wrapped = doc.splitTextToSize(line, doc.internal.pageSize.getWidth() - 30);
    doc.text(wrapped, 15, ty);
    ty += wrapped.length * 5 + 2;
  }
}

/**
 * Main entrypoint. Called from the Editor when the user picks "Export PDF".
 *   state — Zustand snapshot (or exportPayload)
 *   exportRef — React ref pointing to the on-screen canvas wrapper (2D+3D)
 */
export async function exportPDF(state, exportRef, opts = {}) {
  const s = normaliseState(state);
  const rows = computeBOM(s);
  const totals = computeTotals(rows, s);

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  coverPage(doc, s, totals);

  // 2D — proper vector drawing built directly on the page (no screenshot).
  drawPlanPage(doc, s);

  // Multiple 3D camera views. Rendered from the live scene by moving the
  // camera to a preset framing, drawing to an off-screen high-res buffer,
  // then restoring the on-screen state.
  const bounds = computeBounds(s);
  const views = [
    ["se",  "3D · South-East View",  "Right-front perspective — main entry-side view"],
    ["ne",  "3D · North-East View",  "Right-rear perspective — reverse of entry side"],
    ["nw",  "3D · North-West View",  "Left-rear perspective — mirror of NE"],
    ["top", "3D · Top-Down View",    "Bird's-eye camera render"],
  ];
  for (const [v, title, sub] of views) {
    await new Promise((r) => setTimeout(r, 60));
    const url = snap3D(v, bounds);
    await addImagePage(doc, title, sub, url);
  }

  if (rows.length > 0) bomTable(doc, rows);
  summaryPage(doc, s, totals, rows);

  const safeName = (s.projectName || "layout").replace(/[^a-z0-9-_]+/gi, "_");
  doc.save(opts.filename || `${safeName}_report.pdf`);
}
